import { useUser } from '@context/user'
import { getBaseHeaders, getProxyUrl } from '@proxies/index'
import * as Dialog from '@radix-ui/react-dialog'
import * as Form from '@radix-ui/react-form'
import { useAppStore } from '@store'
import { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { useLocation, useNavigate } from 'react-router-dom'
import close2 from '../../assets/generalItems/deleteCrossGray.png'

import { useTrackEvent } from '../../hooks/useAnalytics'

export function Login({ href }: { href: string }) {
  const loginhref = '/planner/login'
  const { userActions } = useUser()
  const navigate = useNavigate()
  const setPrefs = useAppStore.use.setPrefs()
  const closeModal = useAppStore.use.closeModal()
  const openModal = useAppStore.use.openModal()
  const [errorMessage, setErrorMessage] = useState('')
  const { state } = useLocation()
  const { trackLogin, trackEvent } = useTrackEvent()
  const [userState, setUserState] = useState({
    correo: '',
    password: '',
    remember: false,
  })

  const t = (str: string) => (str == null ? '' : String(str))

  const correoRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    openModal()
    return closeModal
  }, [])

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target
    setUserState((prev) => ({ ...prev, [name]: value }))
  }

  const login = async () => {
    // Validar que haya un correo ingresado
    if (!userState.correo || userState.correo.trim() === '') {
      setErrorMessage('Debes escribir un correo para comenzar el proceso')
      return
    }

    try {
      // Track login attempt
      trackEvent('login_attempt', 'user_action', 'login_form_submit')

      const response = await userActions.login(userState, setPrefs, navigate, href)
      if (response && response.error) {
        setErrorMessage(response.error)
        // Track login error
        trackEvent('login_error', 'user_action', response.error)
      } else {
        setErrorMessage('')
        // Track successful login
        trackLogin()
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Error en la autenticación.')
      // Track login error
      trackEvent('login_error', 'user_action', error?.message || 'Unknown error')
    }
  }

  const [modalOn, setModalOn] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const recuperarContrasena = async () => {
    const encodedEmail = encodeURIComponent(userState.correo)
    const url = getProxyUrl(`api/Auth/recuperaContrasenya?correo=${encodedEmail}`)

    try {
      // Track password recovery attempt
      trackEvent('password_recovery_attempt', 'user_action', 'password_recovery_form_submit')

      const response = await fetch(
        url,
        getBaseHeaders({
          method: 'get',
          headers: { Accept: 'text/plain' },
        }),
      )

      if (!response.ok) {
        // Si es un error 500, intentar obtener el detalle del error
        if (response.status === 500) {
          try {
            const errorData = await response.json()
            if (errorData.detail) {
              setModalMessage(t(errorData.detail))
              setModalOn(true)
              // Track password recovery error with specific message
              trackEvent('password_recovery_error', 'user_action', errorData.detail)
              return
            }
          } catch (jsonError) {
            // Si no se puede parsear como JSON, continuar con el manejo genérico
            console.warn('Could not parse error response as JSON:', jsonError)
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await response.text()
      setModalMessage('Se ha enviado un email a la dirección introducida con las instrucciones para restablecer la contraseña.')
      setModalOn(true)
      // Track successful password recovery
      trackEvent('password_recovery_success', 'user_action', 'password_recovery_sent')
    } catch (error) {
      setModalMessage('Debes escribir un correo para comenzar el proceso')
      setModalOn(true)
      // Track password recovery error
      trackEvent('password_recovery_error', 'user_action', 'password_recovery_failed')
    }
  }

  const handleCloseModal = () => {
    setModalOn(false)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nextRef: React.RefObject<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      nextRef?.current?.focus()
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseModal()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <Dialog.Root open={true}>
      <div className='fixed inset-0 bg-white opacity-75'></div>
      <Dialog.Content
        id='login'
        className='absolute left-0 top-0 z-40 grid h-screen w-screen grid-cols-[2fr,1fr] items-center justify-center bg-C/ColorC01 text-left'
        onClick={() => {
          if (modalOn) {
            setModalOn(false)
            return
          }
        }}
      >
        <Dialog.Title className='sr-only'>{'Iniciar Sesión'}</Dialog.Title>
        <Dialog.Description className='sr-only'>{'Iniciar Sesión'}</Dialog.Description>
        <div className='m-10 flex flex-row'>
          <div className='border-colorAtemporal m-auto flex h-[444px] w-[512px] flex-col items-center justify-center border-[1px] bg-C/ColorC01 px-10 py-3 text-left'>
            <div className='relative right-0 top-0 -mr-7 -mt-6 cursor-pointer self-end'>
              <div
                className='flex items-end'
                onClick={() => {
                  navigate(state ?? -1)
                }}
              >
                <img
                  role='button'
                  src={close2}
                />
              </div>
            </div>
            <div className='h-1.5em ml-19 mr-25 mb-15 mb-6 w-full'>
              <p className='text-lg font-normal tracking-tight text-C/ColorC06'>{'¿Ya tienes cuenta?'}</p>
              <p className='text-lg font-normal tracking-tight text-C/ColorC06'>{'Iniciar Sesión'}</p>
            </div>
            <Form.Root className='ml-19 mr-25 flex w-full flex-col gap-y-3'>
              <div className='mt-1'>
                <span className='text-base font-bold text-C/ColorC06'>{'Correo'}</span>
                <input
                  ref={correoRef}
                  maxLength={254}
                  type='email'
                  name='correo'
                  autoComplete='username'
                  className={`border-colorAtemporal my-1 h-8 w-full border-b-2 border-solid py-1 pl-1 text-sm text-C/Color5 outline-C/ColorC11 focus:border-C/ColorC11 focus:bg-C/ColorC09 dark:focus:bg-C/ColorC10 ${userState.correo ? 'rounded' : ''}`}
                  onChange={handleChange}
                  onKeyDown={(e) => handleKeyDown(e, passwordRef)}
                />
              </div>
              <div className='mt-1'>
                <span className='text-base font-bold text-C/ColorC06'>{'Contraseña'} </span>
                <input
                  ref={passwordRef}
                  name='password'
                  type='password'
                  maxLength={20}
                  autoComplete='current-password'
                  className={`border-colorAtemporal my-1 h-8 w-full border-b-2 border-solid  py-1 pl-1 text-C/ColorC06 outline-C/ColorC11 focus:border-C/ColorC11 focus:bg-C/ColorC09 dark:border-C/ColorC11 dark:focus:bg-C/ColorC10 ${userState.password ? 'rounded' : ''}`}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      login()
                    }
                  }}
                />
              </div>

              <span
                className='-mt-3 mb-2 cursor-pointer text-sm text-C/ColorC06'
                onClick={recuperarContrasena}
              >
                {'¿Olvidaste tu contraseña?'}
              </span>
              <div className=' h-3.5em line-height-4 ml-3 flex content-center '>
                <input
                  type='checkbox'
                  value='Remember Me'
                  id='remember'
                  className='mr-1.5 outline-C/ColorC11'
                  defaultChecked={userState.remember}
                  onChange={(e) =>
                    setUserState((prev) => ({ ...prev, remember: e.target.checked }))
                  }
                />
                <label className='display-inline-block mb-0.8 max-w-full text-sm text-C/ColorC06'>
                  {'Recuérdame'}
                </label>
                {errorMessage && (
                  <p className='ml-6 mt-[2.5px] text-sm text-C/Color6'>{errorMessage}</p>
                )}
              </div>
              <Dialog.Close>
                <div
                  className='border-colorAtemporal h-14 w-full cursor-pointer self-center rounded-xl border-[1px] bg-C/Color1 px-4 py-3 text-center shadow-md transition-all hover:border-C/Color4 hover:bg-C/Color2 hover:shadow-lg active:scale-95'
                  onClick={login}
                >
                  <span className='inline-block text-lg font-medium text-C/Color3'>
                    {'Iniciar Sesión'}
                  </span>
                </div>
              </Dialog.Close>
              <Dialog.Close>
                <div
                  role='button'
                  onClick={() => {
                    if (modalOn) return
                    if (href !== loginhref) {
                      navigate('/planner/register')
                    } else navigate('/planner/register')
                  }}
                  className='-mt-2 laptop:hidden desktop:hidden'
                >
                  <span className='cursor-pointer text-sm text-C/Color4'>{'Crear cuenta'}</span>
                </div>
              </Dialog.Close>
            </Form.Root>
          </div>
          <div
            onClick={() => {
              if (modalOn) {
                setModalOn(false)
                return
              }
            }}
            className='border-colorAtemporal m-auto flex h-[444px] w-[512px] flex-col items-center justify-center border-[1px] bg-C/ColorC01 px-10 py-3 text-left tablet:hidden laptop:block desktop:block'
          >
            <div className='h-1.5em ml-19 mr-25 mb-15 mb-6 mt-1.5 w-full'>
              <span className='text-lg font-normal tracking-tight text-C/ColorC06'>
                {'¿No tienes cuenta todavia?'}
              </span>
              <p className=' text-lg text-C/ColorC06'>{'Registrate'}</p>
            </div>

            <Form.Root className='ml-19 mr-25 flex w-full flex-col gap-y-3'>
              <div className='mb-20 mt-1'>
                <span className='text-base font-bold text-C/ColorC06'>{'Registrate y crea tu cuenta en for2home'}</span>
              </div>
              <Dialog.Close>
                <div
                  className='border-colorAtemporal m-auto flex h-14 w-full cursor-pointer items-center justify-center rounded-xl border-[1px] bg-C/Color1 px-4 py-3 shadow-md transition-all hover:border-C/Color4 hover:bg-C/Color2 hover:shadow-lg active:scale-95'
                  onClick={() => {
                    if (modalOn) {
                      setModalOn(false)
                      return
                    }
                    if (href === loginhref) {
                      navigate('/planner/register')
                    } else navigate('/planner/register')
                  }}
                >
                  <p className='inline-block text-lg font-medium text-C/Color3'>{'Registrate'}</p>
                </div>
              </Dialog.Close>
            </Form.Root>
          </div>
        </div>
        <div className=' col-span-1 flex h-full min-w-full bg-bgLogin bg-contain bg-right bg-no-repeat'></div>
      </Dialog.Content>
      <Modal
        isOpen={modalOn}
        onRequestClose={handleCloseModal}
        contentLabel='Recuperación de Contraseña'
        className='relative z-50 mx-auto h-auto w-[95%] max-w-lg rounded-md bg-white px-8 py-10 text-black shadow-xl'
        overlayClassName='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50'
      >
        <div className='relative'>
          <div
            className='absolute -right-7 -top-9 cursor-pointer rounded-full p-2 hover:bg-gray-100'
            onClick={handleCloseModal}
          >
            <img
              role='button'
              src={close2}
              alt='Cerrar modal'
              className='h-3 w-3 brightness-0 filter'
            />
          </div>
          <p className='text-md font-creato-medium text-center'>{modalMessage}</p>
        </div>
      </Modal>
    </Dialog.Root>
  )
}
